import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import {
  DataGridPro,
  GridAlignment,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import dayjs, { Dayjs } from 'dayjs'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { sdk } from '../../config'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../providers'
import { useSendVehicleReminderSmsMutation } from '../../redux/api/vehicleApi'
import {
  AutomatedVehicleReminderInfoTimeframeEnum,
  ResponseError,
  VehicleReminder,
  VehicleReminderMessageInfoTypeEnum
} from '../../sdk'
import { describeVehicle } from '../../utils'
import { defaultReminderMessage } from '../../utils/defaultReminderMessage/defaultReminderMessage'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { getTimeframeStartEnd } from '../../utils/getTimeframeStartEnd/getTimeframeStartEnd'
import { DelayedSendPicker } from '../SharedUI/DelayedSendPicker'
import { useDataGridHover } from '../SharedUI/useDataGridHover'
import { VehicleReminderFilterForm } from './VehicleReminderFilterForm'

interface Props {
  open: boolean
  handleClose: () => void
  vehicleReminders: VehicleReminder[]
  handleSent?: (ids: string[]) => void
}

export const BatchSendReminderDialog: FC<Props> = ({
  open,
  handleClose,
  vehicleReminders,
  handleSent,
  ...props
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { setNotice, setError } = useContext(NotificationContext)
  const [sendVehicleReminderSms] = useSendVehicleReminderSmsMutation()

  const [reminder, setReminder] = useState<VehicleReminderMessageInfoTypeEnum>(
    VehicleReminderMessageInfoTypeEnum.Inspection
  )
  const [timeframe, setTimeframe] =
    useState<AutomatedVehicleReminderInfoTimeframeEnum>(
      AutomatedVehicleReminderInfoTimeframeEnum.NextWeek
    )

  // need useMemo here else memory leak
  const [start, end] = useMemo(() => {
    return getTimeframeStartEnd(timeframe)
  }, [timeframe])

  const filteredReminders = useMemo(() => {
    return (vehicleReminders || [])
      .filter((vr) => {
        const reminderDate =
          reminder === VehicleReminderMessageInfoTypeEnum.Inspection
            ? vr.vehicleEntry?.info?.nextInspectionDate
            : vr.vehicleEntry?.info?.nextOilServiceDate

        if (!reminderDate) {
          return false // Skip if there's no associated date
        }

        return reminderDate >= start && reminderDate <= end
      })
      .filter((vr) => !vr.customerEntries![0]!.information!.marketingOptOut)
  }, [end, reminder, start, vehicleReminders])

  const [phoneStatuses, setPhoneStatuses] = useState<Record<string, string>>({})

  useEffect(() => {
    const newPhoneStatuses: Record<string, string> = {}

    const promises = filteredReminders.map(async (vr) => {
      const phoneNumber = vr.customerEntries![0]!.information!.phone!
      try {
        await sdk.lookupPhoneNumber({
          authorization: accessToken,
          shop: shopId,
          phoneNumber
        })
        newPhoneStatuses[vr.vehicleEntry!.id!] = 'Ready'
      } catch (error) {
        if (error instanceof ResponseError) {
          const errorText = await error.response.text()
          const errorMessage = JSON.parse(errorText).message
          if (errorMessage.includes('Invalid phone number')) {
            newPhoneStatuses[vr.vehicleEntry!.id!] = errorMessage
          } else {
            throw error
          }
        }
      }
    })

    Promise.all(promises).then(() =>
      setPhoneStatuses((prevStatuses) => ({
        ...prevStatuses,
        ...newPhoneStatuses
      }))
    )
  }, [accessToken, filteredReminders, shopId])

  // vehicleId
  const [unchecked, setUnchecked] = useState<string[]>([])

  useEffect(() => {
    if (reminder && timeframe) {
      setUnchecked([])
    }
  }, [reminder, timeframe])

  const checkedIds = useMemo(() => {
    const ids = filteredReminders
      .map((vr) => vr.vehicleEntry!.id!)
      .filter((id) => !unchecked.includes(id))

    return ids
  }, [filteredReminders, unchecked])

  // sets phone numbers with errors to unchecked
  useEffect(() => {
    const errorIds: string[] = []

    checkedIds.forEach((id) => {
      if (id in phoneStatuses && phoneStatuses[id] !== 'Ready') {
        errorIds.push(id)
      }
    })

    setUnchecked((prevUnchecked) => {
      return [...prevUnchecked, ...errorIds]
    })
  }, [checkedIds, phoneStatuses, unchecked])

  const [useDelay, setUseDelay] = useState<boolean>(false)
  const minDateTime = dayjs().add(15, 'minutes')
  const maxDateTime = dayjs().add(1, 'week')
  const [delayedDateTime, setDelayedDateTime] = useState<Dayjs>(
    minDateTime.add(1, 'minutes')
  )
  const [delayedError, setDelayedError] = useState<boolean>(false)

  const handleSend = useCallback(async () => {
    if (delayedError) {
      return
    }

    const sentVehicleIds: string[] = []

    for (const vr of filteredReminders) {
      if (
        checkedIds.find((id) => id === vr.vehicleEntry!.id!) &&
        phoneStatuses[vr.vehicleEntry!.id!] === 'Ready'
      ) {
        await sendVehicleReminderSms({
          authorization: accessToken,
          sendVehicleReminderSmsRequest: {
            info: {
              vehicle: vr.vehicleEntry!.id!,
              type: reminder,
              message: defaultReminderMessage(
                vr.customerEntries![0]!,
                vr.vehicleEntry,
                reminder
              )
            },
            customer: vr.customerEntries![0]!.id!,
            phoneNumber: vr.customerEntries![0]!.information!.phone!,
            sendAt: useDelay ? delayedDateTime.toDate() : undefined
          }
        })

        sentVehicleIds.push(vr.vehicleEntry!.id!)
      }
    }

    if (sentVehicleIds.length >= 1) {
      setNotice('Messages(s) have been sent')

      if (handleSent) {
        handleSent(sentVehicleIds)
      }
    } else {
      setError('No checked/ready recipients')
    }

    handleClose()
  }, [
    delayedError,
    handleClose,
    filteredReminders,
    checkedIds,
    phoneStatuses,
    sendVehicleReminderSms,
    accessToken,
    reminder,
    useDelay,
    delayedDateTime,
    setNotice,
    handleSent,
    setError
  ])

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Checked',
        headerAlign: 'left' as GridAlignment,
        align: 'center' as GridAlignment,
        width: 80,
        getActions: (params: GridRenderCellParams) => {
          return [
            <Checkbox
              key="sendReminder"
              checked={params.row.isChecked}
              onChange={() => {
                setUnchecked((prevUnchecked) => {
                  const idString = params.id as string
                  if (prevUnchecked.includes(idString)) {
                    return prevUnchecked.filter(
                      (uncheckedId) => uncheckedId !== idString
                    )
                  } else {
                    return [...prevUnchecked, idString]
                  }
                })
              }}
              disabled={
                params.row.status !== 'Ready' && params.row.status !== 'Loading'
              }
            />
          ].filter(Boolean)
        }
      },
      {
        field: 'ymm',
        headerName: 'YMM',
        flex: 2
      },
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 2
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'status',
        type: 'actions',
        headerName: 'Status',
        headerAlign: 'left' as GridAlignment,
        align: 'center' as GridAlignment,
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          switch (params.value) {
            case 'Loading':
              return (
                <Tooltip title={params.value}>
                  <Chip label={params.value} color="warning" />
                </Tooltip>
              )

            case 'Ready':
              return (
                <Tooltip title={params.value}>
                  <Chip label={params.value} color="success" />
                </Tooltip>
              )

            default:
              return (
                <Tooltip title={params.value}>
                  <Chip label="Error" color="error" />
                </Tooltip>
              )
          }
        }
      }
    ]
  }, [])

  const rows: GridRowsProp = useMemo(() => {
    return filteredReminders.map((vr) => ({
      id: vr.vehicleEntry?.id,
      ymm: describeVehicle(vr.vehicleEntry?.info),
      customer: formatName(vr.customerEntries![0]!.information!),
      date:
        reminder === VehicleReminderMessageInfoTypeEnum.Inspection
          ? vr.vehicleEntry?.info?.nextInspectionDate
          : vr.vehicleEntry?.info?.nextOilServiceDate,
      isChecked: !unchecked.includes(vr.vehicleEntry!.id!),
      status: phoneStatuses[vr.vehicleEntry!.id!] ?? 'Loading'
    }))
  }, [filteredReminders, phoneStatuses, reminder, unchecked])

  const { popOverProps, handlePopoverClose, setValue, setAnchorEl } =
    useDataGridHover()

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const field = event.currentTarget.dataset.field!
      const id = event.currentTarget.parentElement!.dataset.id!

      if (field !== 'status' && field !== 'actions') {
        const vehicleReminder = filteredReminders.find(
          (vr) => vr.vehicleEntry?.id === id
        )!

        const message = defaultReminderMessage(
          vehicleReminder.customerEntries![0]!,
          vehicleReminder.vehicleEntry,
          reminder
        )

        setValue(message)
        setAnchorEl(event.currentTarget)
      }
    },
    [filteredReminders, reminder, setAnchorEl, setValue]
  )

  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Send Reminder</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Typography pb={2}>
          Update the filters and select the vehicles to send reminders for
        </Typography>

        <Stack direction="row" spacing={5} pb={2}>
          <VehicleReminderFilterForm
            reminder={reminder}
            setReminder={setReminder}
            timeframe={timeframe}
            setTimeframe={setTimeframe}
          />

          <DelayedSendPicker
            useDelay={useDelay}
            setUseDelay={setUseDelay}
            delayedDateTime={delayedDateTime}
            setDelayedDateTime={setDelayedDateTime}
            setError={setDelayedError}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            label="Schedule Timing"
            helperText="Time must be between 15 minutes and 1 week"
          />
        </Stack>

        <DataGridPro
          density="compact"
          rows={rows}
          columns={columns}
          autoHeight
          slotProps={{
            cell: {
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              onClick: handlePopoverClose // needed because anchorEl disables editing on hover
            }
          }}
        />
        <Popover {...popOverProps} />
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="text" onClick={handleSend} disabled={delayedError}>
          Send SMS
        </Button>
      </DialogActions>
    </Dialog>
  )
}
